/** Parse #rgb / #rrggbb / #rrggbbaa into straight RGBA floats (0..1). Cached. */
const cache = new Map<string, readonly [number, number, number, number]>();

export function parseColor(c: string | undefined | null): readonly [number, number, number, number] {
  if (!c) return [0, 0, 0, 0];
  let v = cache.get(c);
  if (v) return v;
  let hex = c.startsWith('#') ? c.slice(1) : c;
  if (hex.length === 3) hex = hex.split('').map((ch) => ch + ch).join('');
  const n = parseInt(hex.slice(0, 6), 16);
  const a = hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
  v = Number.isNaN(n) ? [0, 0, 0, 1] : [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255, a];
  if (cache.size > 4096) cache.clear();
  cache.set(c, v);
  return v;
}

/**
 * Dark canvas (dark theme): invert at 93 % and turn the hue by 180° — white paper turns dark,
 * black ink light, yellow stays yellow. Affine, so it can be applied per primitive before blending.
 */
export function inkRgb(r: number, g: number, b: number): [number, number, number] {
  const ir = 0.93 - 0.86 * r;
  const ig = 0.93 - 0.86 * g;
  const ib = 0.93 - 0.86 * b;
  const c = (v: number) => Math.min(1, Math.max(0, v));
  return [c(-0.574 * ir + 1.43 * ig + 0.144 * ib), c(0.426 * ir + 0.43 * ig + 0.144 * ib), c(0.426 * ir + 1.43 * ig - 0.856 * ib)];
}

/** A CSS color as it looks on the dark canvas (for DOM overlays such as the text editor). */
export function inkCss(c: string): string {
  const [r, g, b, a] = parseColor(c);
  const [x, y, z] = inkRgb(r, g, b);
  return `rgba(${Math.round(x * 255)},${Math.round(y * 255)},${Math.round(z * 255)},${+a.toFixed(4)})`;
}

/** CSS filter with the same effect, for canvases drawn without the renderer (previews, minimap). */
export const INK_FILTER = 'invert(0.93) hue-rotate(180deg)';

export function cssColor(c: string, opacity = 1): string {
  const [r, g, b, a] = parseColor(c);
  return `rgba(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)},${+(a * opacity).toFixed(4)})`;
}
